import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  ApprovalCapabilities,
  ApprovalTaskRow,
  ApprovalTrace,
  ApprovalUserPick
} from '../types/approval';
import type { PageQuery, PageResult } from '../types/result';

/** 审批中心：待办 / 已办 / 轨迹查询；审批动作走 `apply.approve`，干预动作见 remind/transfer/addSigner/… */
export function createApprovalApi(http: HttpClient) {
  return {
    /** 待我审批（ap_task.task_status=PENDING）。 */
    todo(page: PageQuery = {}): Promise<PageResult<ApprovalTaskRow>> {
      return http.request<PageResult<ApprovalTaskRow>>({ path: '/wl/approval/todo', query: toQuery(page) });
    },

    /** 我已审批（ap_task.task_status=DONE）。 */
    done(page: PageQuery = {}): Promise<PageResult<ApprovalTaskRow>> {
      return http.request<PageResult<ApprovalTaskRow>>({ path: '/wl/approval/done', query: toQuery(page) });
    },

    /** 审批实例轨迹：实例 + 任务 + 动作日志。 */
    trace(instanceId: string): Promise<ApprovalTrace> {
      return http.request<ApprovalTrace>({ path: `/wl/approval/trace/${instanceId}` });
    },

    /** 超时治理扫描（管理员 / sys:approval:manage）。 */
    sweepTimeout(limit?: number): Promise<{ scanned: number; handled: number; items: unknown[] }> {
      return http.request<{ scanned: number; handled: number; items: unknown[] }>({
        path: '/wl/approval/timeout/scan',
        method: 'POST',
        query: toQuery({ limit })
      });
    },

    /** 转办 / 加签的人员候选：同机构启用用户，按姓名 / 工号 / 手机号模糊匹配。 */
    searchUsers(keyword?: string, take?: number): Promise<ApprovalUserPick[]> {
      return http.request<ApprovalUserPick[]>({
        path: '/wl/approval/user-search',
        query: toQuery({ keyword, take })
      });
    },

    /** 当前用户在该实例上可执行的动作与流程开关（按钮可见性）。 */
    capabilities(instanceId: Id): Promise<ApprovalCapabilities> {
      return http.request<ApprovalCapabilities>({ path: `/wl/approval/instance/${instanceId}/capabilities` });
    },

    /** 催办：当前节点待办提醒次数 +1 并顺延应处理时间（发起人或 INTERVENE 授权）。 */
    remind(
      instanceId: Id,
      opinion?: string
    ): Promise<{ instanceId: string; nodeCode: string | null; reminded: number; dueAt: string }> {
      return http.request<{ instanceId: string; nodeCode: string | null; reminded: number; dueAt: string }>({
        path: `/wl/approval/instance/${instanceId}/remind`,
        method: 'POST',
        body: { opinion }
      });
    },

    /** 转办：把自己的待办转给他人（流程需 allow_transfer；转他人待办需 INTERVENE）。 */
    transfer(
      taskId: Id,
      body: { toUserId: string; toUserName?: string; opinion?: string }
    ): Promise<{ taskId: string; newTaskId: string; toUserId: string; toUserName: string | null }> {
      return http.request<{ taskId: string; newTaskId: string; toUserId: string; toUserName: string | null }>({
        path: `/wl/approval/task/${taskId}/transfer`,
        method: 'POST',
        body
      });
    },

    /** 加签：当前节点追加审批人（流程需 allow_add_signer）。 */
    addSigner(
      instanceId: Id,
      body: { userId: string; userName?: string; opinion?: string }
    ): Promise<{ instanceId: string; nodeCode: string; taskId: string; userId: string }> {
      return http.request<{ instanceId: string; nodeCode: string; taskId: string; userId: string }>({
        path: `/wl/approval/instance/${instanceId}/add-signer`,
        method: 'POST',
        body
      });
    },

    /** 退回指定节点：只能退回到当前节点之前（流程需 allow_reject_to）。 */
    rejectTo(instanceId: Id, body: { targetNodeCode: string; opinion?: string }): Promise<{
      instanceId: string;
      status: string;
      nodeCode: string | null;
      nodeName: string | null;
      pending: number;
      approvers: string[];
    }> {
      return http.request<{
        instanceId: string;
        status: string;
        nodeCode: string | null;
        nodeName: string | null;
        pending: number;
        approvers: string[];
      }>({
        path: `/wl/approval/instance/${instanceId}/reject-to`,
        method: 'POST',
        body
      });
    },

    /** 终止实例：破坏性操作，需该流程显式 INTERVENE 授权（管理员放行）。 */
    terminate(instanceId: Id, reason?: string): Promise<{ instanceId: string; status: string }> {
      return http.request<{ instanceId: string; status: string }>({
        path: `/wl/approval/instance/${instanceId}/terminate`,
        method: 'POST',
        body: { reason }
      });
    },

    /** 批量审批：流程需 support_batch_approve，单次最多 20 张。 */
    batchApprove(body: {
      applyIds: string[];
      action: 'APPROVE' | 'REJECT';
      opinion?: string;
    }): Promise<{ total: number; succeeded: number; failed: number; items: unknown[] }> {
      return http.request<{ total: number; succeeded: number; failed: number; items: unknown[] }>({
        path: '/wl/approval/batch-approve',
        method: 'POST',
        body
      });
    }
  };
}

export type ApprovalApi = ReturnType<typeof createApprovalApi>;
